import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { FC, useCallback, useEffect } from 'react';
import { useActiveMonth } from 'src/hooks/useActiveMonth';
import { monthsCacheVar } from 'src/infrastructure/local_state';

export interface MonthSelectorProps {
    months: ColDef[];
}

export const MonthSelector: FC<MonthSelectorProps> = ({ months }) => {
    const [activeMonth, setActiveMonth] = useActiveMonth();
    const [monthsCache, setMonthsCache] = useReactiveVar<ColDef[]>(monthsCacheVar);

    useEffect(() => {
        if (months.length <= 0) {
            return;
        }

        if (monthsCache !== months) {
            if (months.length < monthsCache.length) {
                const firstMonth = months[0]?.field?.split('.')[1] ?? '';
                setActiveMonth(firstMonth);
            }
            setMonthsCache(months);
        }
    }, [months, monthsCache, setActiveMonth, setMonthsCache]);

    const handleChange = useCallback(
        (_event: React.MouseEvent<HTMLElement>, newMonth: string) => {
            // it should not be possible to deselect a month
            if (!newMonth) {
                return;
            }
            setActiveMonth(newMonth);
        },
        [setActiveMonth]
    );

    return (
        <Box
            sx={{
                width: '100%',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                },
            }}
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    minWidth: '100%',
                    '& .MuiToggleButtonGroup-root': {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        minWidth: '100%',
                    },
                    '& .MuiToggleButton-root': {
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        whiteSpace: 'nowrap',
                    },
                }}
            >
                <ToggleButtonGroup
                    data-testid="month-selector"
                    value={activeMonth}
                    exclusive
                    onChange={handleChange}
                    aria-label="Months"
                    size="small"
                    sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        width: '100%',
                    }}
                >
                    {months.map(({ field, headerName }, index) => {
                        const fieldName = field?.split('.')[1] ?? '';
                        const [month, year] = headerName?.split(' ') ?? [];
                        return (
                            <ToggleButton
                                data-testid={`month-selector-${fieldName}`}
                                data-monthfield={fieldName}
                                key={fieldName + index}
                                value={fieldName}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {month.slice(0, 3)} <br /> {year}
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>
            </Box>
        </Box>
    );
};
